import {
  type IReference,
  type IReferenceValue,
} from '../references/IReference.ts';
import { ReferencePathDirection } from '../references/IReferencePath.ts';
import { expressionLogger } from '../utils/expressionLogger.ts';

/**
 * Risolve il valore di una reference a partire da un singolo item di un
 * array di oggetti.
 *
 * Analogamente a quanto fa `mapReferenceToPath` per costruire il percorso
 * (concatenando i code dei path items con il code finale della reference),
 * qui navighiamo l'item seguendo i path items con direzione `Child`, per
 * poi accedere alla proprietà finale tramite `ref.code`.
 *
 * Casi gestiti:
 * - Widget standard (es. Text, Select): path vuoto, il valore è
 *   direttamente in `item[ref.code]`.
 * - Widget con valore a oggetto (es. AdvancedSingleSelect): un solo step
 *   `Child`, il valore è in `item[path[0].code][ref.code]`.
 * - Strutture nested su più livelli: più step `Child` consecutivi,
 *   navigati iterativamente.
 *
 * Le altre direction (Parent/Root/Sibling/Absolute) non hanno senso nella
 * risoluzione relativa a un singolo item di array e vengono ignorate.
 */
export function resolveReferenceValueFromItem(
  ref: IReference,
  item: Record<string, IReferenceValue> | null | undefined,
): IReferenceValue | undefined {
  if (item == null) return undefined;

  let current: Record<string, IReferenceValue> = item;

  // Navigo ogni step Child del path per scendere nei sotto-oggetti
  for (const pathItem of ref.path ?? []) {
    // Solo i Child guidano la discesa nell'item; gli altri direction
    // (Parent/Root/Sibling/Absolute) vanno ignorati perché riferiti a un
    // contesto esterno all'item.
    //
    // Nota: in presenza di passi non-Child *ci aspettiamo* di riceverli qui
    // finché il TODO in `getArrayWidgetReference` non viene chiuso: oggi la
    // `structure` del Repeatable viene costruita ereditando il `path` esterno
    // del reference portatore, quindi le reference dei children contengono il
    // prefisso (Parent/Root/Sibling) usato per arrivare al Repeatable. Per
    // questo logghiamo a livello debug e non warn.
    if (pathItem.direction !== ReferencePathDirection.Child) {
      // biome-ignore format: <tbd>
      expressionLogger.debug(`[resolveReferenceValueFromItem] Path item non-Child ignorato (atteso finché il path della structure non viene normalizzato a [])`, { ref, pathItem });
      continue;
    }

    const nested = current[pathItem.code];
    if (nested == null || typeof nested !== 'object' || Array.isArray(nested)) {
      return undefined;
    }

    current = nested as unknown as Record<string, IReferenceValue>;
  }

  return current[ref.code];
}
